import { Link, useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Settings, LogOut, LayoutDashboard, Package, Inbox, CalendarDays, Heart, Menu, X, ArrowRight, Search, Layers, SendHorizonal, Lock, Check, CalendarCheck } from "lucide-react";
import heroImage from "@/assets/landing-hero.jpg";
import focusAiBooking from "@/assets/focus-ai-booking.png";
import focusAiSelectTalent from "@/assets/focus-ai-select-talent.png";
import focusAiTags from "@/assets/focus-ai-tags.png";
import fcLogoWhite from "@/assets/fc-logo-white.png";

import FeaturedTalent from "@/components/FeaturedTalent";
import Footer from "@/components/Footer";
import pitchDeckBrowse from "@/assets/pitch-deck-browse.jpg";
import { StickyLandingNav } from "@/components/landing/StickyLandingNav";

import LiveNetworkSections from "@/components/landing/LiveNetworkSections";
import { fetchWallModels } from "@/lib/featuredWall";


interface Profile {
  first_name: string | null;
  avatar_url: string | null;
  user_type: string;
  agency_id: string | null;
}
const Landing = () => {
  const {
    user,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [trustedAgencies, setTrustedAgencies] = useState<Array<{ id: string; name: string; logo_url: string | null }>>([]);
  const [wallModels, setWallModels] = useState<Array<{ id: string; profile_image_url: string | null }>>([]);
  useEffect(() => {
    supabase
      .from("agencies")
      .select("id, name, logo_url")
      .eq("is_published", true)
      .eq("approval_status", "approved")
      .not("logo_url", "is", null)
      .then(({ data }) => {
        if (data) setTrustedAgencies(data as any);
      });
  }, []);
  useEffect(() => {
    (async () => {
      const { models } = await fetchWallModels(40);
      setWallModels(
        models.map((m) => ({
          id: m.id,
          profile_image_url: m.profile_image_url,
        })),
      );
    })();
  }, []);
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }
      const {
        data
      } = await supabase.from("profiles").select("first_name, avatar_url, user_type, agency_id").eq("id", user.id).maybeSingle();
      setProfile(data);
      setLoading(false);
    };
    fetchProfile();
  }, [user]);
  const isAgency = profile?.user_type === "agency" || !!profile?.agency_id;

  // Redirect authenticated users to their dashboard.
  // Don't gate on `profile` — a missing/legacy profiles row would otherwise
  // strand the user on the public homepage. Default to the client dashboard.
  useEffect(() => {
    if (user && !loading) {
      if (isAgency) {
        navigate("/agency/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, loading, isAgency, navigate]);

  // Show loading state while determining user type (prevents flash)
  if (loading && user) {
    return <div className="min-h-dvh bg-black" />;
  }

  // Show loading while redirecting authenticated users
  if (user) {
    return <div className="min-h-dvh bg-black" />;
  }
  const getInitial = () => {
    if (profile?.first_name) {
      return profile.first_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };
  const displayOrder = ["THE REBELLION", "LA Models", "Mutha", "Rune", "Crawford", "STATE", "The Source Models", "Newmark Models", "Misfit Model Management", "Arquette Agency", "Love Talent Agency", "Focus Agency"];
  const prioritized = displayOrder
    .map((name) => trustedAgencies.find((a) => a.name.toLowerCase().includes(name.toLowerCase())))
    .filter((a): a is NonNullable<typeof a> => a != null && a.logo_url != null);
  const remaining = trustedAgencies.filter(
    (a) => a.logo_url != null && !prioritized.some((p) => p.id === a.id)
  );
  const orderedAgencies = [...prioritized, ...remaining];
  return <>
      {!user && <StickyLandingNav />}
      <SEO
        title="The Casting Platform for Modern Productions"
        description="Focus connects producers and brands with the world's top model agencies. Browse verified fashion models, build casting packages, and book talent with structured requests and secure deposits."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Focus",
            alternateName: "Focus Casting",
            url: "https://focuscasting.com",
            logo: "https://focuscasting.com/favicon.png",
            description: "The casting platform for modern productions, connecting agencies, models and producers.",
            contactPoint: {
              "@type": "ContactPoint",
              email: "info@focuscasting.com",
              contactType: "customer support",
              availableLanguage: ["English"],
            },
            sameAs: [],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Focus",
            url: "https://focuscasting.com",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://focuscasting.com/browse?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      <main className="relative flex min-h-[calc(100dvh-65px)] flex-col items-center justify-center overflow-hidden md:min-h-dvh">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="absolute right-6 top-7 z-30 text-primary-foreground lg:hidden"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-20 bg-black/90 flex flex-col items-center justify-center gap-6 lg:hidden">
            {user ? (
              <>
                <Link to="/browse" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">Browse</Link>
                <Link to="/request-agency" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">Request an Agency</Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">About</Link>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">Dashboard</Link>
                <button onClick={async () => { await signOut(); navigate("/"); setMobileMenuOpen(false); }} className="text-primary-foreground/70 text-lg tracking-[0.15em] uppercase">Sign Out</button>
              </>
            ) : (
              <>
                <Link to="/for-agencies" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">For Agencies</Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">About</Link>
                <Link to="/browse" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">Browse Talent</Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground text-lg tracking-[0.15em] uppercase">Contact</Link>
                <Link to="/auth?mode=login" onClick={() => setMobileMenuOpen(false)} className="text-primary-foreground/70 text-lg tracking-[0.15em] uppercase">Log In</Link>
                <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 text-lg tracking-[0.15em] uppercase rounded-lg">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        )}

        {/* Desktop Navigation */}
        <div className="absolute top-6 right-6 z-20 hidden lg:flex items-center gap-5 xl:gap-6">
          {user ? <>
              <Link to="/browse" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                Browse
              </Link>
              <Link to="/request-agency" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                Request an Agency
              </Link>
              <Link to="/about" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                About
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary-foreground/50">
                    <Avatar className="h-10 w-10 cursor-pointer hover:ring-2 hover:ring-primary-foreground/50 transition-all">
                      {profile?.avatar_url ? <AvatarImage src={profile.avatar_url} alt="Profile" /> : null}
                      <AvatarFallback className="bg-primary-foreground text-primary font-medium">
                        {getInitial()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background border border-border">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard?tab=inbox")} className="cursor-pointer">
                    <Inbox className="h-4 w-4 mr-2" />
                    Inbox
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard?tab=calendar")} className="cursor-pointer">
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Calendar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard?tab=packages")} className="cursor-pointer">
                    <Package className="h-4 w-4 mr-2" />
                    Packages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard?tab=favourites")} className="cursor-pointer">
                    <Heart className="h-4 w-4 mr-2" />
                    Favorites
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard?tab=account")} className="cursor-pointer">
                    <Settings className="h-4 w-4 mr-2" />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => {
                await signOut();
                navigate("/");
              }} className="cursor-pointer text-destructive focus:text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </> : <>
              <Link to="/for-agencies" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                For Agencies
              </Link>
              <Link to="/about" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                About
              </Link>
              <Link to="/browse" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                Browse Talent
              </Link>
              <Link to="/contact" className="text-primary-foreground text-sm tracking-[0.15em] uppercase hover:text-primary-foreground/80 transition-colors duration-300">
                Contact
              </Link>
              <Link to="/auth?mode=login" className="text-primary-foreground/70 text-sm tracking-[0.15em] uppercase hover:text-primary-foreground transition-colors duration-300">
                Log In
              </Link>
              <Link
                to="/auth?mode=signup"
                aria-label="Sign up"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-5 py-2 text-sm tracking-[0.15em] uppercase rounded-lg hover:brightness-110 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                Sign Up
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>}
        </div>
        {/* Background Image */}
        <div className="absolute inset-0">
          <img src={heroImage} alt="Fashion model in elegant attire on the Focus Casting homepage" width={1920} height={1292} className="h-full w-full object-cover object-[22%_center] md:object-center" decoding="async" {...({ fetchpriority: "high" } as any)} />
          <div className="absolute inset-0 bg-gradient-to-l from-black/40 via-black/20 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex h-[calc(100dvh-65px)] w-full max-w-5xl flex-col items-center px-6 pb-5 pt-8 text-center md:mx-auto md:h-auto md:w-auto md:py-24">
          {/* Logo */}
          <img src={fcLogoWhite} alt="Focus Casting" className="h-10 object-contain shadow-none md:mb-12 md:h-16" />


          {/* Main Headline */}
          <h1 className="absolute right-5 top-[31%] max-w-[14rem] text-right font-display text-[2.5rem] font-normal leading-[1.05] text-primary-foreground drop-shadow-lg md:static md:mb-6 md:mt-0 md:max-w-4xl md:text-center md:text-6xl md:font-light md:leading-tight md:drop-shadow-none lg:text-7xl">
            <span className="md:hidden">Cast models<br />from<br /><em className="italic">multiple</em><br />agencies</span>
            <span className="hidden md:inline">Cast models from multiple agencies - <em className="italic text-sidebar">all in one place</em></span>
          </h1>

          {/* Subhead */}
          <p className="hidden max-w-2xl text-base text-primary-foreground/80 md:mb-14 md:block md:text-lg">
            Focus brings agencies, talent, and bookings into one streamlined platform.
          </p>

          {/* Primary CTAs */}
          <div className="mt-auto flex w-full max-w-xs flex-col items-center gap-3 sm:flex-row md:mt-0 md:w-auto md:max-w-none md:gap-6">
            <Link
              to="/browse"
              className="group relative inline-flex w-full items-center justify-center bg-primary-foreground px-8 py-3.5 font-medium tracking-wide text-foreground transition-all duration-300 hover:bg-primary-foreground/90 md:w-auto md:px-10 md:py-5"
            >
              <span className="relative z-10">Find Talent</span>
              <div className="absolute inset-0 border border-primary-foreground translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform" />
            </Link>
            <Link
              to="/auth?mode=signup"
              className="inline-flex w-full items-center justify-center border border-primary-foreground/40 px-8 py-3.5 font-medium tracking-wide text-primary-foreground transition-all duration-300 hover:bg-primary-foreground hover:text-foreground md:w-auto md:px-10 md:py-5"
            >
              Create Free Account
            </Link>
          </div>

          {/* Trust strip - compact icon chips on mobile, full sentence on desktop */}
          <div className="mt-5 hidden flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] tracking-wide text-primary-foreground/90 sm:hidden [&_span]:whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" aria-hidden />
              Stripe secure
            </span>
            <span className="text-primary-foreground/40" aria-hidden>·</span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5" aria-hidden />
              Free to browse
            </span>
            <span className="text-primary-foreground/40" aria-hidden>·</span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" aria-hidden />
              Pay on confirm
            </span>
          </div>
        </div>
       </main>

      {/* Trusted by + Powered by Stripe */}
      <section className="py-16 md:py-20 bg-background border-t border-border/40">
        <div className="container mx-auto px-6 lg:px-8">
          {orderedAgencies.length > 0 && (
            <>
              <div className="flex flex-col items-center text-center mb-10 md:mb-12">
                <p className="text-xs tracking-[0.25em] uppercase font-display text-muted-foreground">
                  TRUSTED BY LEADING AGENCIES
                </p>
                <Link
                  to="/browse?view=agencies"
                  className="mt-3 text-sm font-medium underline underline-offset-4 hover:text-primary transition-colors"
                >
                  See all agencies →
                </Link>
              </div>
              {/* Loose editorial composition - varied logo sizes, centered wrap */}
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8 md:gap-x-14 md:gap-y-12 max-w-5xl mx-auto px-2">
                {orderedAgencies.map((agency, i) => {
                  // Cycle sizes for an organic, non-uniform feel
                  const sizeClass =
                    i % 5 === 0
                      ? "h-12 md:h-16 max-w-[200px] md:max-w-[260px]"
                      : i % 5 === 3
                        ? "h-10 md:h-12 max-w-[170px] md:max-w-[220px]"
                        : "h-7 md:h-9 max-w-[130px] md:max-w-[170px]";
                  return (
                    <Link
                      key={agency.id}
                      to={`/agency/${agency.id}`}
                      title={agency.name}
                      className="group flex items-center justify-center"
                    >
                      <img
                        src={agency.logo_url ?? ""}
                        alt={`${agency.name} logo`}
                        loading="lazy"
                        className={`${sizeClass} w-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300`}
                      />
                    </Link>
                  );
                })}
              </div>
              <div className="h-px w-24 bg-border mx-auto mb-8 mt-12" />
            </>
          )}
          {/* Factual proof points - no fee to browse, clear response expectation */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto text-center">
            {[
              { label: "Verified Agency Rosters", detail: "Every profile is agency-managed" },
              { label: "Structured Booking Requests", detail: "All shoot details in one place" },
              { label: "Contracts Handled In-Platform", detail: "Terms, e-signatures, and usage rights" },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-border bg-background/60 px-4 py-5">
                <p className="font-body text-sm font-semibold text-foreground">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 text-muted-foreground mt-12">
            <span className="text-xs tracking-[0.2em] uppercase">Secured Payments Via</span>
            <svg
              role="img"
              aria-label="Stripe"
              viewBox="0 0 60 25"
              className="h-5 w-auto fill-current text-foreground/80"
            >
              <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 9.01c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C6.31 13.82 0 14.44 0 9.84 0 6.94 2.22 5.2 5.55 5.2c1.55 0 3.1.24 4.66.85v3.88a10.66 10.66 0 0 0-4.66-1.21c-.86 0-1.4.25-1.4.99 0 1.69 6.35.89 6.35 5.93z" />
            </svg>
          </div>
        </div>
      </section>

      {/* Live mosaic hero, 4-step workflow, talent wall */}
      <LiveNetworkSections />

      {/* Mid-page sign-up prompt */}
      <section className="bg-muted/20 border-b border-border">
        <div className="container mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-lg border border-border bg-background px-6 py-6 md:px-10 md:py-8">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-light text-foreground mb-2">
                Save and share your favorite talent
              </h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Create a free account to build packages, save shortlists, and send booking requests.
              </p>
            </div>
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-xs tracking-[0.15em] uppercase hover:bg-primary/90 transition-colors shrink-0"
            >
              Create Free Account
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works - 3 steps */}
      <section className="border-y border-border bg-card">
        <div className="container mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">How it works</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-foreground max-w-3xl mx-auto leading-tight">
              Three steps from brief to <em className="italic">booking</em>
            </h2>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {[
              {
                num: "01",
                icon: Search,
                title: "Search all agencies",
                copy: "Filter the full roster by city, height, look, budget, and availability - across every partner agency.",
                chips: ["City", "Height", "Look", "Budget", "Availability"],
              },
              {
                num: "02",
                icon: Layers,
                title: "Build your package",
                copy: "Save talent from multiple agencies into one package. Share it with your team and align on top picks.",
              },
              {
                num: "03",
                icon: SendHorizonal,
                title: "Send a booking request",
                copy: "One structured request covering budget, usage, fittings, and shoot details - most agencies respond within 48 hours.",
              },
            ].map((step, i) => (
              <div key={step.num} className="relative md:pr-8">
                {i < 2 && (
                  <div className="hidden md:block absolute top-6 right-0 h-px w-8 border-t border-dashed border-border" aria-hidden />
                )}
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="font-display text-3xl md:text-4xl font-light text-primary/40">{step.num}</span>
                  <span className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <step.icon className="h-4 w-4" />
                  </span>
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium text-foreground mb-2">{step.title}</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{step.copy}</p>
                {step.chips && (
                  <div className="mt-4 flex gap-2 overflow-x-auto snap-x snap-mandatory -mx-6 px-6 pb-1 md:mx-0 md:px-0 md:flex-wrap md:overflow-visible">
                    {step.chips.map((c) => (
                      <span
                        key={c}
                        className="snap-start shrink-0 inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Browse preview */}
          <div className="mt-12 md:mt-16 max-w-5xl mx-auto rounded-lg overflow-hidden shadow-2xl border border-border">
            <img
              src={pitchDeckBrowse}
              alt="Focus casting platform showing talent browsing across multiple agencies"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>

        </div>
      </section>

      {/* Pricing / how fees work */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">What it costs</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-foreground max-w-3xl mx-auto leading-tight">
              No subscription. No cost to create account.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Browsing is free", copy: "Search every partner agency, build packages, and share them with your team at no cost." },
              { title: "Requests are free", copy: "Send booking requests to as many agencies as you like. No fee until an agency confirms." },
              { title: "Pay only when you book", copy: "Day rates and usage fees come from the agency. Focus adds a clear service fee of 2%, shown before you confirm." },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-border bg-card p-6 text-left">
                <h3 className="font-display text-xl md:text-2xl font-medium text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Single closing CTA */}
      <section className="py-20 md:py-28 bg-accent border-t border-border">
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 max-w-3xl mx-auto leading-tight">
            Start casting on <span className="italic">Focus</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
            Free to browse, free to request. You only pay when you confirm a booking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-primary/90 transition-colors"
            >
              Create Free Account
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/browse"
              className="inline-flex items-center justify-center gap-2 border border-border text-foreground rounded-lg px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-muted transition-colors"
            >
              Browse Talent
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>;
};
export default Landing;